import React, { useState, useEffect } from 'react';
import ProtectedRoute from '../components/ProtectedRoute';
import DashboardLayout from '../components/DashboardLayout';
import api from '../services/api';

import {
    Search,
    Mail,
    Phone,
    MapPin,
    Eye,
    X
} from 'lucide-react';


const AdminContacts = () => {
    const pageTitle = 'Contact Management';

    const [contacts, setContacts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedContact, setSelectedContact] = useState(null);
    const [showModal, setShowModal] = useState(false);


    // UI State
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        fetchContacts();
    }, []);

    const fetchContacts = async () => {
        try {
            setLoading(true);
            const response = await api.get('/contacts');
            setContacts(response.data || []);

        } catch (err) {
            setError(err.message || 'Failed to load contacts');
        } finally {
            setLoading(false);
        }
    };

    // Filtering
    const filteredContacts = contacts.filter(contact => {
        const searchLower = searchTerm.toLowerCase();
        return (
            contact.firstName?.toLowerCase().includes(searchLower) ||
            contact.lastName?.toLowerCase().includes(searchLower) ||
            contact.email?.toLowerCase().includes(searchLower) ||
            contact.mobile?.includes(searchTerm)
        );
    });

    if (loading) {
        return (
            <ProtectedRoute roles={['ADMIN']}>
                <DashboardLayout title={pageTitle} roles={['ADMIN']}>
                    <div className="flex items-center justify-center min-h-96">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                    </div>
                </DashboardLayout>
            </ProtectedRoute>
        );
    }

    return (
        <ProtectedRoute roles={['ADMIN']}>
            <DashboardLayout roles={['ADMIN']} title={pageTitle}>
                <div className="space-y-6">
                    {/* Header */}
                    <div className="text-center md:text-left space-y-2">
                        <p className="text-gray-500">View and manage contact form submissions.</p>
                    </div>

                    {/* Toolbar */}
                    <div className="flex flex-col md:flex-row gap-4 justify-between items-center bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                        <div className="w-full">
                            {/* Search */}
                            <div className="relative w-full">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                                <input
                                    type="text"
                                    placeholder="Search by name, email, or mobile..."
                                    className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                        </div>
                    </div>

                    {error && (
                        <div className="p-4 bg-red-50 text-red-800 rounded-lg border border-red-100">
                            {error}
                        </div>
                    )}

                    {/* Desktop Table */}
                    <div className="hidden md:block bg-white shadow-sm rounded-xl border border-gray-200 overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-900 text-white">
                                    <tr>
                                        <th className="px-6 py-4 text-left text-xs font-medium uppercase tracking-wider">Name</th>
                                        <th className="px-6 py-4 text-left text-xs font-medium uppercase tracking-wider">Contact Info</th>
                                        <th className="px-6 py-4 text-left text-xs font-medium uppercase tracking-wider">Interest</th>
                                        <th className="px-6 py-4 text-left text-xs font-medium uppercase tracking-wider">Details</th>
                                        <th className="px-6 py-4 text-left text-xs font-medium uppercase tracking-wider">Date</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-100">
                                    {filteredContacts.map((contact, index) => (
                                        <tr key={contact._id || index} className="hover:bg-gray-50 transition-colors">
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm font-medium text-gray-900">{contact.firstName} {contact.lastName}</div>
                                                <div className="text-xs text-gray-500">{contact.collegeOrCompany}</div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center text-sm text-gray-500 mb-1">
                                                    <Mail size={14} className="mr-2" /> {contact.email}
                                                </div>
                                                <div className="flex items-center text-sm text-gray-500">
                                                    <Phone size={14} className="mr-2" /> {contact.mobile}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm text-gray-900">{contact.trainingInterest}</div>
                                                <span className={`px-2 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full ${contact.preference === 'Online' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
                                                    }`}>
                                                    {contact.preference}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-500">
                                                <div className="flex items-center justify-between max-w-xs gap-2">
                                                    <span className="truncate block">{contact.message || '-'}</span>
                                                    {contact.message && (
                                                        <button
                                                            onClick={() => {
                                                                setSelectedContact(contact);
                                                                setShowModal(true);
                                                            }}
                                                            className="text-indigo-600 hover:text-indigo-800 flex-shrink-0"
                                                            title="View full message"
                                                        >
                                                            <Eye size={16} />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>

                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {new Date(contact.createdAt).toLocaleDateString()}
                                            </td>
                                        </tr>
                                    ))}
                                    {filteredContacts.length === 0 && (
                                        <tr>
                                            <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                                                No contacts found matching your search.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Mobile Card View */}
                    <div className="md:hidden space-y-4">
                        {filteredContacts.map((contact, index) => (
                            <div key={contact._id || index} className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
                                <div className="flex justify-between items-start mb-4">
                                    <div>
                                        <h3 className="font-semibold text-gray-900">{contact.firstName} {contact.lastName}</h3>
                                        <p className="text-sm text-gray-500">{contact.collegeOrCompany}</p>
                                    </div>
                                    <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${contact.preference === 'Online' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
                                        }`}>
                                        {contact.preference}
                                    </span>
                                </div>

                                <div className="space-y-2 text-sm mb-4">
                                    <div className="flex items-center text-gray-600">
                                        <Mail size={16} className="mr-2" /> {contact.email}
                                    </div>
                                    <div className="flex items-center text-gray-600">
                                        <Phone size={16} className="mr-2" /> {contact.mobile}
                                    </div>
                                    <div className="flex items-center text-gray-600">
                                        <MapPin size={16} className="mr-2" /> {contact.address || 'No address provided'}
                                    </div>
                                    <div className="pt-2 border-t border-gray-100 mt-2">
                                        <p className="font-medium text-gray-700">Interest: <span className="font-normal">{contact.trainingInterest}</span></p>
                                    </div>
                                    {contact.message && (
                                        <div className="pt-2">
                                            <p className="text-gray-500 italic truncate">"{contact.message}"</p>
                                        </div>
                                    )}
                                </div>

                                <div className="flex justify-between items-center pt-3 border-t border-gray-100 mt-2">
                                    <div className="text-xs text-gray-400">
                                        Received: {new Date(contact.createdAt).toLocaleDateString()}
                                    </div>
                                    <button
                                        onClick={() => {
                                            setSelectedContact(contact);
                                            setShowModal(true);
                                        }}
                                        className="text-indigo-600 text-sm font-medium flex items-center hover:text-indigo-800"
                                    >
                                        <Eye size={16} className="mr-1" /> View Details
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Contact Details Modal */}
                    {showModal && selectedContact && (
                        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
                            <div className="relative bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                                <div className="p-6 border-b border-gray-100 flex justify-between items-center sticky top-0 bg-white z-10">
                                    <h3 className="text-xl font-bold text-gray-900">Contact Details</h3>
                                    <button
                                        onClick={() => setShowModal(false)}
                                        className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100 transition-colors"
                                    >
                                        <X size={24} />
                                    </button>
                                </div>
                                <div className="p-6 space-y-6">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div>
                                            <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">Full Name</p>
                                            <p className="text-gray-900 font-medium text-lg">{selectedContact.firstName} {selectedContact.lastName}</p>
                                        </div>
                                        <div>
                                            <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">Submitted On</p>
                                            <p className="text-gray-900">{new Date(selectedContact.createdAt).toLocaleString()}</p>
                                        </div>
                                        <div>
                                            <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">Email</p>
                                            <div className="flex items-center text-gray-700">
                                                <Mail size={16} className="mr-2 text-gray-400" />
                                                <a href={`mailto:${selectedContact.email}`} className="hover:text-indigo-600">{selectedContact.email}</a>
                                            </div>
                                        </div>
                                        <div>
                                            <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">Mobile</p>
                                            <div className="flex items-center text-gray-700">
                                                <Phone size={16} className="mr-2 text-gray-400" />
                                                <a href={`tel:${selectedContact.mobile}`} className="hover:text-indigo-600">{selectedContact.mobile}</a>
                                            </div>
                                        </div>
                                        <div>
                                            <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">Training Interest</p>
                                            <p className="text-gray-900 font-medium">{selectedContact.trainingInterest}</p>
                                        </div>
                                        <div>
                                            <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">Preference</p>
                                            <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${selectedContact.preference === 'Online' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
                                                }`}>
                                                {selectedContact.preference}
                                            </span>
                                        </div>
                                        <div className="md:col-span-2">
                                            <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">College / Company</p>
                                            <p className="text-gray-900">{selectedContact.collegeOrCompany || 'N/A'}</p>
                                        </div>
                                        <div className="md:col-span-2">
                                            <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">Address</p>
                                            <div className="flex items-start text-gray-700">
                                                <MapPin size={16} className="mr-2 mt-1 text-gray-400 flex-shrink-0" />
                                                <span>{selectedContact.address || 'N/A'}</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div>
                                        <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Message</p>
                                        <div className="bg-gray-50 p-4 rounded-xl border border-gray-100 text-gray-700 whitespace-pre-wrap">
                                            {selectedContact.message || 'No message provided.'}
                                        </div>
                                    </div>
                                </div>
                                <div className="p-6 border-t border-gray-100 flex justify-end">
                                    <button
                                        onClick={() => setShowModal(false)}
                                        className="px-6 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors font-medium"
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                </div>
            </DashboardLayout>
        </ProtectedRoute>
    );
};

export default AdminContacts;
